import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, inject, Injectable } from '@angular/core';

import { map } from 'rxjs';

import { mapInfiniteScrollResponse } from '@lib/core/helpers/partner.helper';
import { InfiniteScrollResponse } from '@lib/core/models/infinite-scroll-response.interface';
import { PROTECTED_API_URL } from '@lib/core/tokens/api.tokens';

import { TaskAssignPayload, TaskResolvePayload, TaskResponse, TaskSearchPayload } from '../models/task.interface';
import { TaskAdapterService } from './task-adapter.service';

@Injectable()
export class TasksService {
  private readonly http = inject(HttpClient);
  private readonly adapter = inject(TaskAdapterService);

  private readonly protectedApiUrl: string;

  constructor(@Inject(PROTECTED_API_URL) protectedApiUrl: string) {
    this.protectedApiUrl = protectedApiUrl;
  }

  searchTasks(payload: TaskSearchPayload) {
    return this.http.post<InfiniteScrollResponse<TaskResponse>>(`${this.protectedApiUrl}/actions/search`, payload).pipe(
      map(response => mapInfiniteScrollResponse(response, r => this.adapter.toTask(r))),
    );
  }

  assignTask(actionId: string, createdAt: Date, payload: TaskAssignPayload) {
    const params = new HttpParams().set('createdAt', createdAt.toISOString());
    return this.http.post<TaskResponse>(`${this.protectedApiUrl}/actions/${actionId}/assign`, payload, { params }).pipe(
      map(r => this.adapter.toTask(r)),
    );
  }

  startTask(actionId: string, createdAt: Date) {
    const params = new HttpParams().set('createdAt', createdAt.toISOString());
    return this.http.post<TaskResponse>(`${this.protectedApiUrl}/actions/${actionId}/start`, null, { params }).pipe(
      map(r => this.adapter.toTask(r)),
    );
  }

  resolveTask(actionId: string, createdAt: Date, payload: TaskResolvePayload) {
    const params = new HttpParams().set('createdAt', createdAt.toISOString());
    return this.http.post<TaskResponse>(`${this.protectedApiUrl}/actions/${actionId}/resolve`, payload, { params }).pipe(
      map(r => this.adapter.toTask(r)),
    );
  }

  createTask(payload: unknown) {
    return this.http.post<TaskResponse>(`${this.protectedApiUrl}/actions`, payload).pipe(
      map(r => this.adapter.toTask(r)),
    );
  }

  getTaskById(actionId: string, createdAt?: Date) {
    let params = new HttpParams();
    if (createdAt) {
      params = params.set('createdAt', createdAt.toISOString());
    }
    return this.http.get<TaskResponse>(`${this.protectedApiUrl}/actions/${actionId}`, { params }).pipe(
      map(r => this.adapter.toTask(r)),
    );
  }

  getTaskHistory(actionId: string, createdAt: Date) {
    const params = new HttpParams().set('createdAt', createdAt.toISOString());
    return this.http.get<TaskResponse[]>(`${this.protectedApiUrl}/actions/${actionId}/history`, { params }).pipe(
      map(responses => responses.map(r => this.adapter.toTask(r))),
    );
  }
}
